// <NEW FILE> Store for user-customized tag types and artist update tracking
"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { TagCategory } from "./tag-taxonomy-store"

export interface TagCustomization {
  tag: string
  customCategory: TagCategory
  isFavoritedArtist?: boolean
  lastViewedAt?: number
  hasNewContent?: boolean
}

interface TagCustomizationsStore {
  customizations: Record<string, TagCustomization>
  setTagCategory: (tag: string, category: TagCategory) => void
  getTagCategory: (tag: string) => TagCategory | null
  markArtistViewed: (tag: string) => void
  setArtistHasNewContent: (tag: string, hasNew: boolean) => void
  getHasNewContent: (tag: string) => boolean
  removeCustomization: (tag: string) => void
}

export const useTagCustomizationsStore = create<TagCustomizationsStore>()(
  persist(
    (set, get) => ({
      customizations: {},

      setTagCategory: (tag: string, category: TagCategory) => {
        const cleanTag = tag.toLowerCase().trim()
        set((state) => ({
          customizations: {
            ...state.customizations,
            [cleanTag]: {
              ...state.customizations[cleanTag],
              tag: cleanTag,
              customCategory: category,
            },
          },
        }))
      },

      getTagCategory: (tag: string) => {
        const cleanTag = tag.toLowerCase().trim()
        return get().customizations[cleanTag]?.customCategory || null
      },

      markArtistViewed: (tag: string) => {
        const cleanTag = tag.toLowerCase().trim()
        set((state) => ({
          customizations: {
            ...state.customizations,
            [cleanTag]: {
              ...state.customizations[cleanTag],
              tag: cleanTag,
              lastViewedAt: Date.now(),
              hasNewContent: false,
            },
          },
        }))
      },

      setArtistHasNewContent: (tag: string, hasNew: boolean) => {
        const cleanTag = tag.toLowerCase().trim()
        const custom = get().customizations[cleanTag]
        if (custom?.customCategory === "artist") {
          set((state) => ({
            customizations: {
              ...state.customizations,
              [cleanTag]: {
                ...state.customizations[cleanTag],
                hasNewContent: hasNew,
              },
            },
          }))
        }
      },

      getHasNewContent: (tag: string) => {
        const cleanTag = tag.toLowerCase().trim()
        return get().customizations[cleanTag]?.hasNewContent || false
      },

      removeCustomization: (tag: string) => {
        const cleanTag = tag.toLowerCase().trim()
        set((state) => {
          const { [cleanTag]: _, ...rest } = state.customizations
          return { customizations: rest }
        })
      },
    }),
    {
      name: "tag-customizations",
    },
  ),
)
